import type { Attributes } from "@opentelemetry/api";
import { OTLPLogExporter } from "@opentelemetry/exporter-logs-otlp-http";
import { Resource } from "@opentelemetry/resources";
import {
  BatchLogRecordProcessor,
  LoggerProvider,
} from "@opentelemetry/sdk-logs";

import type { MeProfile } from "@elation/api-client/src/stitch-web";

import {
  APP_ENV,
  APP_VERSION,
  DATADOG_SERVICE,
  IS_AUTOMATION,
  IS_TEST,
  MOCKING_ENABLED,
  OTEL_CLIENT_KEY,
  OTEL_COLLECTOR_URL,
} from "@src/env";

import { datadogLogs } from "./datadog";

// Follows ECS User + Elation practice semantic conventions required by the
// Snowflake ingestion pipeline. See: frostbyte/product_analytics_staging.sql
type OtelUser = {
  "user.id": string;
  "user.roles": MeProfile["type"][];
  "practice.id": number;
};

let currentUser: OtelUser | undefined;
let otelLogger: ReturnType<LoggerProvider["getLogger"]> | undefined;

// Mirrors hippo's nestedToDotKeys: recursively flattens nested objects to
// dot-notation keys and normalizes whitespace in key names to underscores.
// e.g. { user: { id: "42" } } → { "user.id": "42" }
// e.g. { "start encounter": 1 } → { "start_encounter": 1 }
function flattenAttributes(
  obj: Record<string, unknown>,
  prefix = "",
): Record<string, unknown> {
  return Object.entries(obj).reduce(
    (acc, [key, value]) => {
      const normalizedKey = key.replace(/\s+/g, "_");
      const flatKey = prefix ? `${prefix}.${normalizedKey}` : normalizedKey;
      if (
        value !== null &&
        typeof value === "object" &&
        !Array.isArray(value)
      ) {
        Object.assign(
          acc,
          flattenAttributes(value as Record<string, unknown>, flatKey),
        );
      } else {
        acc[flatKey] = value;
      }
      return acc;
    },
    {} as Record<string, unknown>,
  );
}

function init(): void {
  if (IS_TEST) return;
  // Automation sessions are excluded from product analytics. If they are ever
  // included, use user_agent.synthetic.type: "test" to tag them.
  if (IS_AUTOMATION) return;
  if (MOCKING_ENABLED) return;
  if (!OTEL_CLIENT_KEY) return;
  if (typeof window === "undefined") return;

  try {
    const provider = new LoggerProvider({
      resource: new Resource({
        "service.name": DATADOG_SERVICE,
        "service.version": APP_VERSION,
        "deployment.environment": APP_ENV,
      }),
    });

    provider.addLogRecordProcessor(
      new BatchLogRecordProcessor(
        new OTLPLogExporter({
          url: `${OTEL_COLLECTOR_URL}/v1/logs`,
          headers: { Authorization: `Bearer ${OTEL_CLIENT_KEY}` },
        }),
      ),
    );

    otelLogger = provider.getLogger(DATADOG_SERVICE);

    window.addEventListener("beforeunload", () => {
      provider.forceFlush().catch((err) => {
        console.warn(
          "[otel] forceFlush failed on page unload; buffered events may be lost:",
          err,
        );
      });
    });
  } catch (err) {
    console.warn("[otel] failed to initialize OTEL logger provider:", err);
    datadogLogs.logger.warn(
      "[otel] failed to initialize OTEL logger provider",
      { err },
    );
  }
}

init();

export function setOtelUser(user: OtelUser): void {
  currentUser = user;
}

export function clearOtelUser(): void {
  currentUser = undefined;
}

export function emitProductAnalyticsEvent(
  name: string,
  properties: Record<string, unknown> | undefined,
): void {
  if (!otelLogger) return;
  try {
    otelLogger.emit({
      body: `User Action: ${name}`,
      severityNumber: 9, // SeverityNumber.INFO per OTEL spec
      severityText: "INFO",
      attributes: flattenAttributes({
        "event.type": "action",
        "event.name": name,
        "event.id": crypto.randomUUID(),
        "meta.snowflake.enabled": true,
        "user_agent.original": navigator.userAgent,
        ...(properties ?? {}),
        ...(currentUser ?? {}), // user identity always wins over event-specific keys
      }) as Attributes,
    });
  } catch (err) {
    console.warn("[otel] emitProductAnalyticsEvent failed:", err);
  }
}
